import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { IEvent } from "../../../interfaces/models/IEvent";
import {
  TabEnum,
  TabType,
  TabRoutes,
  SetupTypeEnum,
} from "../../../Enums/EventEnums";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import chevronRightButton from "../../../assets/Fonts/chevron-right-button.svg";
import ChevronDown from "../../../components/Atomos/Icons/ChevronDown";
import AddIcon from "../../../assets/images&icons/add-icon.svg";
import ImageHandler from "../../../components/Image/ImageHandler";
import { UseFirmList } from "../../../hooks/Catalogs/useFirm";
import { UseGroupList } from "../../../hooks/Catalogs/useGroupList";
import { getNowInDateTimeZone } from "../../../utils/dates";
import { TimezoneEnum } from "../../../Enums";
import ComingSoon from "./ComingSoon";
import ColorLogo from "../../../components/Layout/Logo/ColorLogo";
import { useAddToCalendar } from "../../../hooks/Events/useAddToCalendar";
import inPersonImage from "../../../assets/images&icons/in-person-detail.png";
import virtualImage from "../../../assets/images&icons/virtual-detail.png";
import hybridImage from "../../../assets/images&icons/hibrid-detail.png";
import { formatEventDate } from "../../../utils/eventDateFormatter";
import { formatEventLocation } from "../../../utils/eventLocationFormatter";
import { LinkifiedPlainText } from "../../../utils/LinkifiedPlainText";
import PrintIcon from "../../../components/Atomos/Icons/PrintIcon";
import { useEventDetailData } from "../../../hooks/Events/EventDetailDataContext";
import "../../../styles/printStyles.css";

const hostedByLogoClassName =
  "flex w-[250px] min-w-0 shrink-0 items-center justify-center overflow-hidden";

interface EventDetailsLayoutProps {
  event: IEvent;
  children?: React.ReactNode;
  activeTabOverride?: TabType;
}

export default function EventDetailsLayout ({
  event,
  children,
  activeTabOverride,
}: EventDetailsLayoutProps) {
  const navigate = useNavigate();
  const { id, tab: currentTabUrl } = useParams();
  const { firmData } = UseFirmList();
  const { groupData } = UseGroupList();
  const handleAddToCalendar = useAddToCalendar(event);
  const { ensureAttendeesLoaded, attendeesState } = useEventDetailData();
  const printRef = useRef<HTMLDivElement>(null);

  const scrollContainersRef = useRef<Array<{ element: HTMLElement; originalOverflow: string; originalHeight: string; originalMaxHeight: string }>>([]);
  const restoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPrintingRef = useRef<boolean>(false);

  const restoreScrollStyles = () => {
    if (!isPrintingRef.current) {
      return;
    }

    if (restoreTimeoutRef.current) {
      clearTimeout(restoreTimeoutRef.current);
      restoreTimeoutRef.current = null;
    }

    scrollContainersRef.current.forEach(({ element, originalOverflow, originalHeight, originalMaxHeight }) => {
      if (element && element.style) {
        element.style.overflow = originalOverflow || '';
        element.style.height = originalHeight || '';
        element.style.maxHeight = originalMaxHeight || '';
      }
    });

    if (document.body) {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.maxHeight = '';
    }
    if (document.documentElement) {
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.documentElement.style.maxHeight = '';
    }

    scrollContainersRef.current = [];
    isPrintingRef.current = false;
  };

  useEffect(() => {
    const mediaQueryList = window.matchMedia('print');

    const handleMediaChange = (mql: MediaQueryList | MediaQueryListEvent) => {
      if (!mql.matches && isPrintingRef.current) {
        restoreScrollStyles();
      }
    };

    const handleWindowFocus = () => {
      if (isPrintingRef.current) {
        setTimeout(() => {
          restoreScrollStyles();
        }, 50);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && isPrintingRef.current) {
        setTimeout(() => {
          restoreScrollStyles();
        }, 50);
      }
    };

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleMediaChange);
    } else {
      (mediaQueryList as any).addListener(handleMediaChange);
    }

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', handleMediaChange);
      } else {
        (mediaQueryList as any).removeListener(handleMediaChange);
      }
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
      }
      restoreScrollStyles();
    };
  }, []);

  const [firmLogos, setFirmLogos] = useState<
    {
      id: string;
      logo: string | null;
      smallLogo?: string | null;
      name: string;
    }[]
  >([]);
  const [groupLogos, setGroupLogos] = useState<
    {
      id: string;
      logo: string | null;
      smallLogo?: string | null;
      name: string;
    }[]
  >([]);
  const [showWSGLogo, setShowWSGLogo] = useState(false);
  const [groupBannerFallback, setGroupBannerFallback] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (
      event?.eventHostSponsor &&
      event.eventHostSponsor.toLowerCase().includes("wsg")
    ) {
      setShowWSGLogo(true);
    } else {
      setShowWSGLogo(false);
    }
    if (event?.sponsorHost && event.sponsorHost.length > 0) {
      const memberSponsors = event.sponsorHost.filter(
        (sponsor) => sponsor.type === "member"
      );

      const groupSponsors = event.sponsorHost.filter(
        (sponsor: any) => sponsor.type === "wsg_group"
      );
      if (memberSponsors.length > 0) {
        if (firmData.length > 0) {
          const logos = memberSponsors.map((sponsor) => {
            const firm = firmData.find((f: any) => f.id === sponsor.id);
            return firm
              ? {
                id: firm.id,
                logo: firm.logo,
                smallLogo: firm.smallLogo,
                name: firm.name || firm.firmName,
              }
              : {
                id: sponsor.id,
                logo: null,
                smallLogo: null,
                name: "Unknown Firm",
              };
          });
          setFirmLogos(logos);
        } else {
          const placeholders = memberSponsors.map((sponsor) => ({
            id: sponsor.id,
            logo: null,
            smallLogo: null,
            name: "Loading...",
          }));
          setFirmLogos(placeholders);
        }
      } else {
        setFirmLogos([]);
      }

      if (groupSponsors.length > 0) {
        if (groupData.length > 0) {
          const logos = groupSponsors
            .map((sponsor: any) => {
              const group = groupData.find((g: any) => g.id === sponsor.id);
              return group && group.title
                ? {
                  id: group.id || sponsor.id,
                  logo: group.logo || null,
                  smallLogo: group.logo || null,
                  name: group.title,
                }
                : null;
            })
            .filter((logo): logo is NonNullable<typeof logo> => logo !== null);
          setGroupLogos(logos);

          if (!event.bannerDetails) {
            const firstSponsor = groupSponsors[0];
            const firstGroup = groupData.find(
              (g: any) => g.id === firstSponsor.id
            );
            if (firstGroup && firstGroup.bannerPhoto) {
              setGroupBannerFallback(firstGroup.bannerPhoto);
            } else {
              setGroupBannerFallback(null);
            }
          } else {
            setGroupBannerFallback(null);
          }
        } else {
          const placeholders = groupSponsors.map((sponsor: any) => ({
            id: sponsor.id,
            logo: null,
            smallLogo: null,
            name: "Loading...",
          }));
          setGroupLogos(placeholders);
          setGroupBannerFallback(null);
        }
      } else {
        if (event.sponsorHost && event.sponsorHost.length > 0) {
          if (groupData.length > 0) {
            const logos = event.sponsorHost
              .map((sponsor: any) => {
                const group = groupData.find((g: any) => g.id === sponsor.id);
                return group && group.title
                  ? {
                    id: group.id || sponsor.id,
                    logo: group.logo || null,
                    smallLogo: group.logo || null,
                    name: group.title,
                  }
                  : null;
              })
              .filter(
                (logo): logo is NonNullable<typeof logo> => logo !== null
              );
            setGroupLogos(logos);

            if (!event.bannerDetails && event.sponsorHost.length > 0) {
              const firstGroup = groupData.find(
                (g: any) => g.title === event.sponsorHost![0].id
              );
              if (firstGroup && firstGroup.bannerPhoto) {
                setGroupBannerFallback(firstGroup.bannerPhoto);
              } else {
                setGroupBannerFallback(null);
              }
            } else {
              setGroupBannerFallback(null);
            }
          } else {
            const placeholders = event.sponsorHost.map((sponsor: any) => ({
              id: sponsor.id,
              logo: null,
              smallLogo: null,
              name: "Loading...",
            }));
            setGroupLogos(placeholders);
            setGroupBannerFallback(null);
          }
        } else {
          setGroupLogos([]);
          setGroupBannerFallback(null);
        }
      }
    } else {
      setFirmLogos([]);
      setGroupLogos([]);
      setGroupBannerFallback(null);
    }
  }, [
    event?.sponsorHost,
    event?.eventHostSponsor,
    firmData,
    groupData,
    event?.bannerDetails,
  ]);

  const sortedTabs = event?.tabs || [];

  const getTabType = (
    tab: TabType | { type: TabType; index: number }
  ): TabType => {
    return typeof tab === "string" ? tab : tab.type;
  };

  const isTabObject = (
    tab: TabType | { type: TabType; index: number }
  ): tab is { type: TabType; index: number } => {
    return typeof tab === "object" && tab !== null && "index" in tab;
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  };

  const eventTimezoneValue = event?.timeZone
    ? TimezoneEnum[event.timeZone as keyof typeof TimezoneEnum] ||
    event.timeZone
    : "UTC";

  const nowInEventZoneMs = getNowInDateTimeZone(eventTimezoneValue);
  const eventStartDateMs = event?.startDate ? event.startDate * 1000 : null;
  const hasRegistrationLink = Boolean(event.registrationLink?.trim());
  const shouldShowRegistration =
    eventStartDateMs !== null &&
    nowInEventZoneMs < eventStartDateMs &&
    (event.eventType !== "virtual" || hasRegistrationLink);

  const isOneDayAfterEvent = (): boolean => {
    if (!event.startDate) {
      return false;
    }

    const eventEndDate = event.endDate || event.startDate;
    const eventEndDateMs = eventEndDate * 1000;
    const oneDayAfterEventMs = eventEndDateMs + 24 * 60 * 60 * 1000;

    return nowInEventZoneMs >= oneDayAfterEventMs;
  };

  const shouldHideTab = (tabType: TabType): boolean => {
    if (!isOneDayAfterEvent()) {
      return false;
    }

    const setupType = event.setupType;
    if (setupType === "regional") {
      return (
        tabType === "overview" ||
        tabType === "registration" ||
        tabType === "accomodation"
      );
    }

    if (setupType === "networking" || setupType === "group") {
      return tabType === "registration" || tabType === "accomodation";
    }

    return false;
  };

  const processedTabs = sortedTabs
    .map((tab, idx) => {
      const tabType = getTabType(
        tab as TabType | { type: TabType; index: number }
      );
      const tabObj = tab as TabType | { type: TabType; index: number };
      const index = isTabObject(tabObj) ? tabObj.index : idx;
      return { type: tabType, index };
    })
    .filter((tab) => {
      if (
        ((event.setupType === "webinar" || event.setupType === "groupWebinar") || (event.memberEventsTab && event.registrationLink)) &&
        tab.type === "registration"
      ) {
        return false;
      }
      return true;
    })
    .filter((tab) => {
      return !shouldHideTab(tab.type);
    })
    .sort((a, b) => a.index - b.index);

  const handleTabClick = (tabType: TabType) => {
    const tabRoute = TabRoutes[tabType];
    navigate(`/events/${id}/${tabRoute}`);
  };

  const isTabActive = (tabType: TabType) => {
    if (activeTabOverride) {
      return tabType === activeTabOverride;
    }
    const tabRoute = TabRoutes[tabType];
    return currentTabUrl === tabRoute;
  };

  const getCurrentTab = (): TabType | null => {
    if (activeTabOverride) {
      return activeTabOverride;
    }
    if (currentTabUrl) {
      const entry = Object.entries(TabRoutes).find(([_, route]) => route === currentTabUrl);
      return entry ? (entry[0] as TabType) : null;
    }
    return null;
  };

  const getDefaultEventImage = () => {
    if (event.eventType === "virtual") {
      return virtualImage;
    } else if (event.eventType === "hybrid") {
      return hybridImage;
    } else if (event.eventType === "inPerson") {
      return inPersonImage;
    }
    return inPersonImage;
  };

  const currentTab = getCurrentTab();
  const isAttendeesTab = currentTab === "attendees";
  const isAttendeesTabEnabled = event.tabs?.some((tab) => {
    const tabType = typeof tab === "string" ? tab : tab.type;
    return tabType === "attendees";
  });

  useEffect(() => {
    if (!isAttendeesTab || !isAttendeesTabEnabled || !event?.id) {
      return;
    }

    void ensureAttendeesLoaded().catch(() => {
      // Attendees tab handles its own error state.
    });
  }, [ensureAttendeesLoaded, event?.id, isAttendeesTab, isAttendeesTabEnabled]);

  const hasAttendees =
    attendeesState.status === "success"
      ? attendeesState.data.length > 0
      : attendeesState.status === "error"
        ? null
        : null;


  return (
    <div ref={printRef} className="print-content min-h-screen bg-white">
      <div className="relative w-full h-[57px] overflow-hidde">
        <div className="absolute top-6 left-6 z-10 no-print">
          <LegacyButton
            text="All Events"
            content="textIcon"
            icon={
              <div className="w-6 h-6 bg-[#0070CD] rounded-full flex items-center justify-center chevron-white-icon">
                <ChevronDown
                  className="fill-white"
                  style={{ width: "10px", height: "10px", color: "white" }}
                  rotation="rotate(90deg)"
                />
              </div>
            }
            color="accent"
            style="ghost"
            className="!p-0 bg-white bg-opacity-20 font-bold !text-[#004578] [&>div]:!justify-start [&>div]:gap-3 !text-[14px] lg:text-base"
            iconOrder="iconFirst"
            onClick={() => navigate("/events")}
          />
        </div>
        <div className="absolute top-6 right-6 z-10 no-print">
        </div>
      </div>
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl md:text-4xl font-[500] text-primary mb-8 font-decimal text-left">
          {event.title}
        </h1>
        <div className="h-px bg-[#EBEBEB] mb-8"></div>
        <div className="bg-white overflow-hidden mb-8">
          <div className="flex flex-col lg:flex-row w-full">
            <div className="mb-8 lg:mb-0 lg:w-[30%] flex-shrink-0">
              <ImageHandler
                src={event.bannerDetails || groupBannerFallback || ""}
                alt={event.title}
                className="w-full object-cover"
                fallback={getDefaultEventImage()}
              />
            </div>
            <div className="lg:pl-8 lg:flex-1 w-full">
              <div className="flex flex-col md:flex-row md:items-start mb-2 lg:mb-6 w-full">
                <div className="flex-1">
                  <div className="flex items-start mb-2">
                    <span className="text-base font-semibold text-primary mr-2 lg:text-lg font-decimal">
                      When:
                    </span>
                    <span className="text-base lg:text-lg text-gray-font">
                      {formatEventDate(event)}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      <span className="text-base font-semibold text-primary mr-2 lg:text-lg font-decimal">
                        {event.setupType === "webinar" ||
                          event.setupType === "groupWebinar"
                          ? "Type:"
                          : "Where:"}
                      </span>
                      <span className="text-base lg:text-lg text-gray-font">
                        {event.setupType === "webinar" ||
                          event.setupType === "groupWebinar"
                          ? "Virtual"
                          : formatEventLocation(event)}
                      </span>
                    </div>
                    {event.eventType === "hybrid" && (
                      <div className="flex items-center mt-1">
                        <span className="text-base font-semibold text-primary mr-2 lg:text-lg font-decimal">
                          Type:
                        </span>
                        <span className="text-base lg:text-lg text-gray-font">
                          Virtual
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-start mt-2 md:mt-0 lg:justify-end no-print">
                  {!event.isPastEvent && !event.comingSoonDate && (
                    <LegacyButton
                      text="ADD TO CALENDAR"
                      content="textIcon"
                      icon={
                        <div className="w-6 h-6 bg-[#0070CD] rounded-full flex items-center justify-center">
                          <img src={AddIcon} alt="add" className="w-2 h-2" />
                        </div>
                      }
                      color="accent"
                      className="!p-0 [&>div]:!justify-start [&>div]:gap-3 !text-[14px] font-bold !text-[#004578]"
                      style="ghost"
                      onClick={handleAddToCalendar}
                    />
                  )}
                </div>
              </div>
              <p className="text-gray-font leading-normal mb-6 text-left text-base lg:text-lg font-normal whitespace-pre-line">
                <LinkifiedPlainText text={event.description || ""} />
              </p>
              {shouldShowRegistration && (
                <div className="flex justify-end no-print">
                  <LegacyButton
                    text={
                      event.setupType === "webinar" ||
                        event.setupType === "groupWebinar"
                        ? "VIRTUAL REGISTRATION"
                        : "REGISTER"
                    }
                    content="textIcon"
                    icon={
                      <img
                        src={chevronRightButton}
                        alt="chevron"
                        className="ml-1 w-3 h-3"
                      />
                    }
                    color="primary"
                    onClick={() => {
                      if (
                        (event.setupType === "webinar" ||
                          event.setupType === "groupWebinar" ||
                          event.memberEventsTab) &&
                        event.registrationLink
                      ) {
                        let link = event.registrationLink || "";
                        if (
                          link &&
                          !link.startsWith("http://") &&
                          !link.startsWith("https://")
                        ) {
                          link = "https://" + link;
                        }
                        window.open(link, "_blank");
                        return;
                      }

                      const isWebinarSetup =
                        event.setupType === "webinar" ||
                        event.setupType === "groupWebinar";

                      if (
                        !isWebinarSetup && event.eventType === "inPerson" &&
                        getCurrentTab() === "registration"
                      ) {
                        navigate(`/events/${event.id}/registration/form`);
                      } else {
                        navigate(`/events/${event.id}/registration`);
                      }
                    }}
                    className={`!w-full ${event.setupType === "webinar" ||
                      event.setupType === "groupWebinar"
                      ? "md:!w-auto"
                      : "md:!w-[205px]"
                      }`}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {processedTabs.length > 0 && (
          <div className="border-b border-[#999999] mb-8 border-solid no-print">
            <nav className="events-tabs-container">
              {processedTabs.map((tab) => {
                const tabType = tab.type;
                const isActive = isTabActive(tabType);
                return (
                  <button
                    key={`${tabType}-${tab.index}`}
                    onClick={() => handleTabClick(tabType)}
                    className={
                      isActive
                        ? "members-tab-button-active flex items-center gap-2"
                        : "members-tab-button flex items-center gap-2"
                    }
                  >
                    <span>{TabEnum[tabType] || tabType}</span>
                    {isActive ? (
                      <ChevronDown
                        className="fill-white"
                        style={{ width: "14px", height: "14px" }}
                      />
                    ) : (
                      <ChevronDown
                        className="fill-white"
                        rotation="rotate(-90deg)"
                        style={{ width: "14px", height: "14px" }}
                      />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        )}
        {(() => {
          if (!currentTab) {
            return children;
          }

          const tabConfig = event.tabs?.find((tab) => {
            const tabType = typeof tab === "string" ? tab : tab.type;
            return tabType === currentTab;
          });

          const isComingSoon = typeof tabConfig === "object" && tabConfig?.comingSoon;

          const shouldShowComingSoonForAttendees =
            isAttendeesTab &&
            tabConfig !== undefined &&
            hasAttendees === false;

          if (isComingSoon || shouldShowComingSoonForAttendees) {
            return <ComingSoon title={currentTab} />;
          }

          return children;
        })()}
      </div>
      {(firmLogos.length > 0 || groupLogos.length > 0 || showWSGLogo) && (
        <div className="py-4 border-t border-b border-[#EBEBEB] mt-6 px-4 sm:px-6 lg:px-8 no-print">
          <p className="text-2xl leading-normal font-semibold mb-2 font-decimal text-primary uppercase">
            Hosted by:
          </p>
          {firmLogos.length + groupLogos.length + (showWSGLogo ? 1 : 0) ===
            1 ? (
            <div className="flex justify-center w-full">
              {showWSGLogo && (
                <div
                  key="wsg-logo-single"
                  className={hostedByLogoClassName}
                >
                  <div className="flex h-full w-full min-h-0 min-w-0 items-center justify-center text-primary">
                    <ColorLogo />
                  </div>
                </div>
              )}
              {firmLogos.length === 1 && (
                <div
                  key={`firm-0`}
                  className={hostedByLogoClassName}
                >
                  {firmLogos[0].smallLogo || firmLogos[0].logo ? (
                    <img
                      src={firmLogos[0].smallLogo || firmLogos[0].logo || ""}
                      alt={firmLogos[0].name}
                      className="h-full w-full min-h-0 min-w-0 object-contain"
                      onError={(e) => {
                        if (
                          firmLogos[0].smallLogo &&
                          firmLogos[0].logo &&
                          e.currentTarget.src !== firmLogos[0].logo
                        ) {
                          e.currentTarget.src = firmLogos[0].logo;
                        } else {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.classList.remove(
                            "hidden"
                          );
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${firmLogos[0].smallLogo || firmLogos[0].logo
                      ? "hidden"
                      : ""
                      }`}
                  >
                    <span className="text-xs text-gray-500 text-center px-1">
                      {firmLogos[0].name}
                    </span>
                  </div>
                </div>
              )}
              {groupLogos.length === 1 && (
                <div
                  key={`group-0`}
                  className={hostedByLogoClassName}
                >
                  {groupLogos[0].smallLogo || groupLogos[0].logo ? (
                    <img
                      src={groupLogos[0].smallLogo || groupLogos[0].logo || ""}
                      alt={groupLogos[0].name}
                      className="h-full w-full min-h-0 min-w-0 object-contain"
                      onError={(e) => {
                        if (
                          groupLogos[0].smallLogo &&
                          groupLogos[0].logo &&
                          e.currentTarget.src !== groupLogos[0].logo
                        ) {
                          e.currentTarget.src = groupLogos[0].logo;
                        } else {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.classList.remove(
                            "hidden"
                          );
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${groupLogos[0].smallLogo || groupLogos[0].logo
                      ? "hidden"
                      : ""
                      }`}
                  >
                    <span className="text-xs text-gray-500 text-center px-1">
                      {groupLogos[0].name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-w-0 justify-around items-center gap-6">
              {showWSGLogo && (
                <div
                  key="wsg-logo-multiple"
                  className={hostedByLogoClassName}
                >
                  <div className="flex h-full w-full min-h-0 min-w-0 items-center justify-center text-primary">
                    <ColorLogo />
                  </div>
                </div>
              )}
              {firmLogos.map((firm, index) => (
                <div
                  key={`firm-${index}`}
                  className={hostedByLogoClassName}
                >
                  {firm.smallLogo || firm.logo ? (
                    <img
                      src={firm.smallLogo || firm.logo || ""}
                      alt={firm.name}
                      className="h-full w-full min-h-0 min-w-0 object-contain"
                      onError={(e) => {
                        if (
                          firm.smallLogo &&
                          firm.logo &&
                          e.currentTarget.src !== firm.logo
                        ) {
                          e.currentTarget.src = firm.logo;
                        } else {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.classList.remove(
                            "hidden"
                          );
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${firm.smallLogo || firm.logo ? "hidden" : ""
                      }`}
                  >
                    <span className="text-xs text-gray-500 text-center px-1">
                      {firm.name}
                    </span>
                  </div>
                </div>
              ))}

              {groupLogos.map((group, index) => (
                <div
                  key={`group-${index}`}
                  className={hostedByLogoClassName}
                >
                  {group.smallLogo || group.logo ? (
                    <img
                      src={group.smallLogo || group.logo || ""}
                      alt={group.name}
                      className="h-full w-full min-h-0 min-w-0 object-contain"
                      onError={(e) => {
                        if (
                          group.smallLogo &&
                          group.logo &&
                          e.currentTarget.src !== group.logo
                        ) {
                          e.currentTarget.src = group.logo;
                        } else {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.classList.remove(
                            "hidden"
                          );
                        }
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full bg-gray-100 rounded flex items-center justify-center ${group.smallLogo || group.logo ? "hidden" : ""
                      }`}
                  >
                    <span className="text-xs text-gray-500 text-center px-1">
                      {group.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex my-10 px-4 sm:px-6 lg:px-8 mb-10 pb-10">
        <div className="flex gap-4">
          <div className="w-4 h-4 bg-primary rounded-full"></div>
          <div className="w-4 h-4 bg-primary rounded-full"></div>
          <div className="w-4 h-4 bg-primary rounded-full"></div>
        </div>
      </div>
    </div>
  );
}
